/* ────────────────────────────────────────────────────────────────
 *  DropdownSearchInfrastructureAlert.jsx
 *  • Non-admins see ONLY their primary campus(es) (Users.campusIDs)
 *  • Admins see all buildings
 *  • “All Buildings” becomes “All My Campuses (N)” for non-admins
 *  • Keeps existing fast seed + slow fallback + context writes
 *  • NEW: if no primary campus exists after fallback, signal parent
 * ──────────────────────────────────────────────────────────────── */
import React, { useState, useEffect, useContext, useRef } from 'react';
import { Button, Modal, FormControl, Spinner, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios             from '../axiosAlertConfig';
import { UserContext }   from '../../userAlertContext';
import Styles            from './DropdownSearchInfrastructureAlert.module.css';

/* helpers */
const up = s => (s ?? '').toString().toUpperCase();
const idsFromSel = (v, list) =>
  !v               ? []
  : v === '-99'    ? list.map(r => r.iBuildingID)            // “All (my) campuses” sentinel
                   : v.split(',').filter(Boolean);

const asArray = (value) =>
  Array.isArray(value) ? value : value == null || value === '' ? [] : [value];

export default function DropdownSearchInfrastructureAlert({
  databaseContent = [],             // rows from BuildingInfos
  selectedID      = '',             // controlled value from parent
  onSelect,                         // ({id,name}) => void
  onMissingPrimaryCampus,           // NEW: parent callback
}) {
  const {
    user,                /* FULL user object */
    setUser,             /* <── we overwrite campusIDs/names here on fallback */
    setIBuildingID,
    setBuildingName,
  } = useContext(UserContext);

  /* role gate */
  const isAdmin = ['administrator','systemadministrator']
    .includes((user?.userRole || '').toLowerCase());

  const searchInputRef = useRef(null);
  const userLookupKey = `${user?.iAccountID ?? ''}:${user?.userID ?? ''}`;

  /* ───── basic ui state ───── */
  const [show,    setShow]    = useState(false);
  const [search,  setSearch]  = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // NEW: allows one fallback lookup per logged-in user
  const [primaryCampusLookupDone, setPrimaryCampusLookupDone] = useState(false);

  const [sel,     setSel]     = useState({
    id:   sessionStorage.getItem('defaultCampusID')   || '',
    name: sessionStorage.getItem('defaultCampusName') || 'Campus'
  });

   const [checked, setChecked] = useState('');

  // NEW: reset fallback lookup state whenever the logged-in user changes
  useEffect(() => {
    setPrimaryCampusLookupDone(false);
  }, [userLookupKey]);

  // NEW: clear stale stored/default campus state before signaling parent
  const clearCampusSelection = () => {
    setShow(false);
    setChecked([]);
    setSel({ id: '', name: 'Campus' });
    setIBuildingID('');
    setBuildingName('');
    sessionStorage.removeItem('defaultCampusID');
    sessionStorage.removeItem('defaultCampusName');
    onSelect?.({ id: '', name: '' });
  };

  // NEW: single path for “no primary campus”
 const signalMissingPrimaryCampus = () => {
  toast.error('No primary campus is assigned to this user.');
  clearCampusSelection();
  onMissingPrimaryCampus?.();
};

  /* ────────────────────────────────────────────────────────────
   *  1️⃣ FAST-seed from user.campusIDs[0] / campusNames[0]
   * ──────────────────────────────────────────────────────────── */
  useEffect(() => {
    const id   = user?.campusIDs?.[0];
    const name = user?.campusNames?.[0] ?? 'Campus';

    if (!id) return;

    setPrimaryCampusLookupDone(true);

    if (id !== sel.id || name !== sel.name) {
      setSel({ id, name });
      setIBuildingID(id);
      setBuildingName(name);
      onSelect?.({ id, name });
    }
  }, [user?.campusIDs, user?.campusNames]); // keep existing behavior

  /* ────────────────────────────────────────────────────────────
   *  2️⃣ SLOW fallback – fetch from DB once if UserContext still
   *     has no primary campus.
   *
   *     IMPORTANT:
   *     Do NOT skip this just because sessionStorage already has
   *     a defaultCampusID. That stale value is exactly what allows
   *     users with no primary campus to slip through.
   * ──────────────────────────────────────────────────────────── */
  useEffect(() => {
    const contextPrimaryCampusID = user?.campusIDs?.[0];

    if (contextPrimaryCampusID || primaryCampusLookupDone || !user?.iAccountID || !user?.userID) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const { data } = await axios.get('/UsersManagement/primaryCampusByUser', {
          params: { iAccountID: user.iAccountID, iUserID: user.userID },
        });

        if (cancelled) return;

        const campusIDs   = asArray(data?.campusIDs   ?? data?.campusID);
        const campusNames = asArray(data?.campusNames ?? data?.campusName);

        const id   = campusIDs[0]   ?? '';
        const name = campusNames[0] ?? 'Campus';

        /* ⬇️ PATCH BACK INTO USER CONTEXT when found */
        if (campusIDs.length) {
          setUser(prev => {
            const merged = { ...(prev || {}), campusIDs, campusNames };
            sessionStorage.setItem('userInfo', JSON.stringify(merged)); // persistence
            return merged;
          });
        }

        /* populate local selection when found */
        if (id) {
          setSel({ id, name });
          setIBuildingID(id);
          setBuildingName(name);
          onSelect?.({ id, name });
          sessionStorage.setItem('defaultCampusID', id);
          sessionStorage.setItem('defaultCampusName', name);
        } else {
          // NEW: this is the exact no-primary-campus condition
          signalMissingPrimaryCampus();
        }
      } catch (e) {
        console.error('[Dropdown] primaryCampusByUser', e);
        setError('Network error');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setPrimaryCampusLookupDone(true);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [
    primaryCampusLookupDone,
    user?.campusIDs,
    user?.iAccountID,
    user?.userID,
    setUser,
    setIBuildingID,
    setBuildingName,
  ]);

  /* ────────────────────────────────────────────────────────────
   *  Filter visible list based on role:
   *    • Admins → all buildings.
   *    • Non-admins → ONLY their campusIDs.
   *  Also compute sentinel label (“All Buildings” vs “All My Campuses (N)”)
   * ──────────────────────────────────────────────────────────── */
  const allowedIDs = React.useMemo(() => {
    const allIDs  = databaseContent.map(r => String(r.iBuildingID));
    const userIDs = Array.isArray(user?.campusIDs) ? user.campusIDs.map(String) : [];
    return isAdmin ? allIDs : userIDs;
  }, [databaseContent, user?.campusIDs, isAdmin]);

  const allowedRows = React.useMemo(
    () => databaseContent.filter(r => allowedIDs.includes(String(r.iBuildingID))),
    [databaseContent, allowedIDs]
  );
  

  const sentinelLabel = isAdmin
    ? 'All Buildings'
    : `All My Campuses (${allowedRows.length})`;

  // List of selectable rows (no sentinel inside the checkbox list)
  const list = React.useMemo(() => {
    // only allowed rows for this user
    return [...allowedRows];
  }, [allowedRows]);

  /* ────────────────────────────────────────────────────────────
   *  3️⃣ Parent → child sync (use **filtered list** for names)
   * ──────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!selectedID) return;

    const ids =
      selectedID === '-99'
        ? list.map(r => r.iBuildingID) // "All My Campuses" after filtering
        : selectedID.split(',').filter(Boolean);

    const names = list
      .filter(r => ids.includes(r.iBuildingID))
      .map(r => r.BuildingName);

    const nm = selectedID === '-99' ? sentinelLabel : names.join(', ');

    if (selectedID !== sel.id || nm !== sel.name) {
      setSel({ id: selectedID, name: nm });
    }
  }, [selectedID, list, sentinelLabel]); // avoid sel.* to prevent loops

  /* prepare check-boxes whenever modal opens */
 useEffect(() => {
  if (!show) return;

  if (sel.id === '-99') {
    setChecked('-99');
  } else {
    setChecked(sel.id || '');
  }
}, [show, sel.id]);




  /* helpers */
 const toggleCheck = (id) => {
  setChecked(id);
};

const toggleAll = () => {
  setChecked('-99');
};



const applySel = (id, name, idsArray = null, listRef = []) => {
  setSel({ id, name });

  const firstRealIDs = Array.isArray(idsArray) ? idsArray : [];
  const firstID =
    firstRealIDs.find(x => x && x !== '-99') ||
    ((id && id !== '-99' && !id.includes(',')) ? id : (user?.campusIDs?.[0] ?? ''));

  if (firstID) {
    const firstName =
      listRef.find(r => r.iBuildingID === firstID)?.BuildingName || name || 'Campus';
    setIBuildingID(firstID);
    setBuildingName(firstName);
  }

  onSelect?.({ id, name });
  sessionStorage.setItem('defaultCampusID', id);
  sessionStorage.setItem('defaultCampusName', name);
};

const applyMulti = () => {
  if (!checked) return;

  if (checked === '-99') {
    applySel('-99', sentinelLabel, list.map(r => r.iBuildingID), list);
  } else {
    const nm = list.find(r => r.iBuildingID === checked)?.BuildingName || '';
    applySel(checked, nm, [checked], list);
  }

  setShow(false);
};

  const filtered = list.filter(r =>
    r.BuildingName.toLowerCase().includes(search.toLowerCase()),
  );

  const titleText = React.useMemo(() => {
    if (!sel.id) return sel.name;
    if (sel.id === '-99') return sentinelLabel;
    const ids = idsFromSel(sel.id, list);
    const names = list.filter(r => ids.includes(r.iBuildingID)).map(r => r.BuildingName);
    return names.join(', ');
  }, [sel.id, sel.name, list, sentinelLabel]);

  /* ─── UI ─── */
  return (
    <>
      <Button
        variant="outline-primary"
        className={Styles.toggleBtn}
        title={titleText}
        style={{
          boxSizing: 'border-box',
          width: '100%',
          padding: 4,
          fontSize: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 0 // ← required for flex+ellipsis
        }}
        onClick={() => list.length && sel.id && setShow(true)}
      >
        <span
          style={{
            display: 'inline-block',
            maxWidth: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'center'
          }}
        >
          { sel.id ? up(sel.name) : <Spinner animation="border" size="sm" /> }
        </span>
      </Button>

      <Modal show={show} onHide={() => setShow(false)} fullscreen="sm-down" centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Campus / Building</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ paddingTop: 0 }}>
          <div className="px-3 py-2">
            <FormControl
              ref={searchInputRef}
              placeholder="Search"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* “All Buildings” for admins, “All My Campuses (N)” for non-admins */}
 <Form.Check
  type="radio"
  name="campusSelection"
  id="chk_all"
  className="px-3 py-2 fw-bold"
  label={sentinelLabel}
  checked={checked === '-99'}
  onChange={toggleAll}
/>

          {loading && (
            <div className="d-flex justify-content-center py-3">
              <Spinner animation="border" size="sm"/>
            </div>
          )}
          {error && !loading && <div className="text-danger px-3 py-2">{error}</div>}

          {!loading && !error && (
            filtered.length
              ? filtered.map(r => (
                  <div key={r.iBuildingID}
                       className="d-flex align-items-center border-bottom w-100"
                       style={{ fontSize: 14, lineHeight: 1.3, padding: '8px 12px' }}>
                   
 <Form.Check
  type="radio"
  name="campusSelection"
  id={`chk_${r.iBuildingID}`}
  className="me-2"
  checked={checked === r.iBuildingID}
  onChange={() => toggleCheck(r.iBuildingID)}
/>
<span
  role="button"
  className="flex-grow-1 text-truncate"
  onClick={() => toggleCheck(r.iBuildingID)}
>
  {r.BuildingName}
</span>

                  </div>
                ))
              : <div className="px-3 py-2">No matching records</div>
          )}
        </Modal.Body>

        <Modal.Footer className="justify-content-between">
          <Button variant="secondary"
                   onClick={() => { setChecked(''); setShow(false); }} >
            Cancel
          </Button>
          <Button variant="primary"
                  disabled={!checked.length}
                  onClick={applyMulti}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

// /* ────────────────────────────────────────────────────────────────
//  *  DropdownSearchInfrastructureAlert.jsx
//  *  • Non-admins see ONLY their primary campus(es) (Users.campusIDs)
//  *  • Admins see all buildings
//  *  • “All Buildings” becomes “All My Campuses (N)” for non-admins
//  *  • Keeps existing fast seed + slow fallback + context writes
//  * ──────────────────────────────────────────────────────────────── */
// import React, { useState, useEffect, useContext, useRef } from 'react';
// import { Button, Modal, FormControl, Spinner, Form } from 'react-bootstrap';
// import axios             from '../axiosAlertConfig';
// import { UserContext }   from '../../userAlertContext';
// import Styles            from './DropdownSearchInfrastructureAlert.module.css';

// /* helpers */
// const up = s => (s ?? '').toString().toUpperCase();
// const idsFromSel = (v, list) =>
//   !v               ? []
//   : v === '-99'    ? list.map(r => r.iBuildingID)            // “All (my) campuses” sentinel
//                    : v.split(',').filter(Boolean);

// export default function DropdownSearchInfrastructureAlert({
//   databaseContent = [],             // rows from BuildingInfos
//   selectedID      = '',             // controlled value from parent
//   onSelect,                         // ({id,name}) => void
// }) {
//   const {
//     user,                /* FULL user object */
//     setUser,             /* <── we overwrite campusIDs/names here on fallback */
//     setIBuildingID,
//     setBuildingName,
//   } = useContext(UserContext);

//   /* role gate */
//   const isAdmin = ['administrator','systemadministrator']
//     .includes((user?.userRole || '').toLowerCase());

//   /* ───── basic ui state ───── */
//   const [show,    setShow]    = useState(false);
//   const [search,  setSearch]  = useState('');
//   const [loading, setLoading] = useState(false);
//   const [error,   setError]   = useState(null);

//   const [sel,     setSel]     = useState({
//     id:   sessionStorage.getItem('defaultCampusID')   || '',
//     name: sessionStorage.getItem('defaultCampusName') || 'Campus'
//   });

//   const [checked, setChecked] = useState([]);

//   /* ────────────────────────────────────────────────────────────
//    *  1️⃣ FAST-seed from user.campusIDs[0] / campusNames[0]
//    * ──────────────────────────────────────────────────────────── */
//   useEffect(() => {
//     const id   = user?.campusIDs?.[0];
//     const name = user?.campusNames?.[0];
//     if (id && id !== sel.id) {
//       setSel({ id, name: name ?? 'Campus' });
//       setIBuildingID(id);
//       setBuildingName(name ?? 'Campus');
//       onSelect?.({ id, name: name ?? 'Campus' });
//     }
//   }, [user?.campusIDs, user?.campusNames]); // reruns only if arrays change

//   /* ────────────────────────────────────────────────────────────
//    *  2️⃣ SLOW fallback – fetch from DB **once** if still blank
//    *     and patch back into UserContext
//    * ──────────────────────────────────────────────────────────── */
//   useEffect(() => {
//     if (sel.id || !user?.iAccountID || !user?.userID) return; // already have data
//     let cancelled = false;
//     (async () => {
//       try {
//         setLoading(true);
//         const { data } = await axios.get('/UsersManagement/primaryCampusByUser', {
//           params: { iAccountID: user.iAccountID, iUserID: user.userID },
//         });
//         if (cancelled) return;

//         const campusIDs   = Array.isArray(data.campusIDs)   ? data.campusIDs   : [];
//         const campusNames = Array.isArray(data.campusNames) ? data.campusNames : [];

//         const id   = campusIDs[0]   ?? '';
//         const name = campusNames[0] ?? 'Campus';

//         /* ⬇️  PATCH BACK INTO USER CONTEXT  */
//         if (campusIDs.length) {
//           setUser(prev => {
//             const merged = { ...prev, campusIDs, campusNames };
//             sessionStorage.setItem('userInfo', JSON.stringify(merged)); // persistence
//             return merged;
//           });
//         }

//         /* populate local selection */
//         if (id) {
//           setSel({ id, name });
//           setIBuildingID(id);
//           setBuildingName(name);
//           onSelect?.({ id, name });
//           sessionStorage.setItem('defaultCampusID', id);
//           sessionStorage.setItem('defaultCampusName', name);
//         }
//       } catch (e) {
//         console.error('[Dropdown] primaryCampusByUser', e);
//         setError('Network error');
//       } finally {
//         setLoading(false);
//       }
//     })();
//     return () => { cancelled = true; };
//   }, [sel.id, user?.iAccountID, user?.userID, setUser, setIBuildingID, setBuildingName, onSelect]);

//   /* ────────────────────────────────────────────────────────────
//    *  Filter visible list based on role:
//    *    • Admins → all buildings.
//    *    • Non-admins → ONLY their campusIDs.
//    *  Also compute sentinel label (“All Buildings” vs “All My Campuses (N)”)
//    * ──────────────────────────────────────────────────────────── */
//   const allowedIDs = React.useMemo(() => {
//     const allIDs  = databaseContent.map(r => String(r.iBuildingID));
//     const userIDs = Array.isArray(user?.campusIDs) ? user.campusIDs.map(String) : [];
//     return isAdmin ? allIDs : userIDs;
//   }, [databaseContent, user?.campusIDs, isAdmin]);

//   const allowedRows = React.useMemo(
//     () => databaseContent.filter(r => allowedIDs.includes(String(r.iBuildingID))),
//     [databaseContent, allowedIDs]
//   );

//   const sentinelLabel = isAdmin
//     ? 'All Buildings'
//     : `All My Campuses (${allowedRows.length})`;

//   // List of selectable rows (no sentinel inside the checkbox list)
//   const list = React.useMemo(() => {
//     // only allowed rows for this user
//     return [...allowedRows];
//   }, [allowedRows]);

//   /* ────────────────────────────────────────────────────────────
//    *  3️⃣ Parent → child sync (use **filtered list** for names)
//    * ──────────────────────────────────────────────────────────── */
//   useEffect(() => {
//     if (!selectedID) return;

//     const ids =
//       selectedID === '-99'
//         ? list.map(r => r.iBuildingID) // "All My Campuses" after filtering
//         : selectedID.split(',').filter(Boolean);

//     const names = list
//       .filter(r => ids.includes(r.iBuildingID))
//       .map(r => r.BuildingName);

//     const nm = selectedID === '-99' ? sentinelLabel : names.join(', ');

//     if (selectedID !== sel.id || nm !== sel.name) {
//       setSel({ id: selectedID, name: nm });
//     }
//   }, [selectedID, list, sentinelLabel]); // avoid sel.* to prevent loops

//   /* prepare check-boxes whenever modal opens */
//   useEffect(() => {
//     if (show) setChecked(idsFromSel(sel.id, list));
//   }, [show, sel.id, list]);

//   /* helpers */
//   const toggleCheck = id =>
//     setChecked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

//   const toggleAll = () => {
//     setChecked(p => (p.length === list.length ? [] : list.map(r => r.iBuildingID)));
//   };

//   const applySel = (id, name, idsArray = null, listRef = []) => {
//     setSel({ id, name });

//     // For global context, keep a single id (first real building)
//     const firstRealIDs = Array.isArray(idsArray) ? idsArray : [];
//     const firstID =
//       firstRealIDs.find(x => x && x !== '-99') ||
//       ((id && id !== '-99' && !id.includes(',')) ? id : (user?.campusIDs?.[0] ?? ''));

//     if (firstID) {
//       const firstName =
//         listRef.find(r => r.iBuildingID === firstID)?.BuildingName || name || 'Campus';
//       setIBuildingID(firstID);
//       setBuildingName(firstName);
//     }

//     onSelect?.({ id, name });
//     sessionStorage.setItem('defaultCampusID', id);
//     sessionStorage.setItem('defaultCampusName', name);
//   };

//   const applyMulti = () => {
//     if (!checked.length) return;

//     if (checked.length === list.length) {
//       // “All” = all **allowed** rows only (no sentinel id included)
//       applySel('-99', sentinelLabel, list.map(r => r.iBuildingID), list);
//     } else if (checked.length === 1) {
//       const id = checked[0];
//       const nm = list.find(r => r.iBuildingID === id)?.BuildingName || '';
//       applySel(id, nm, [id], list);
//     } else {
//       const ids = checked;
//       const names = list
//         .filter(r => ids.includes(r.iBuildingID))
//         .map(r => r.BuildingName);
//       applySel(ids.join(','), names.join(', '), ids, list);
//     }

//     setShow(false);
//   };

//   const filtered = list.filter(r =>
//     r.BuildingName.toLowerCase().includes(search.toLowerCase()),
//   );

//   const titleText = React.useMemo(() => {
//     if (!sel.id) return sel.name;
//     if (sel.id === '-99') return sentinelLabel;
//     const ids = idsFromSel(sel.id, list);
//     const names = list.filter(r => ids.includes(r.iBuildingID)).map(r => r.BuildingName);
//     return names.join(', ');
//   }, [sel.id, sel.name, list, sentinelLabel]);

//   /* ─── UI ─── */
//   return (
//     <>
//       <Button
//         variant="outline-primary"
//         className={Styles.toggleBtn}
//         title={titleText}
//         style={{
//           boxSizing: 'border-box',
//           width: '100%',
//           padding: 4,
//           fontSize: 16,
//           display: 'flex',
//           alignItems: 'center',
//           justifyContent: 'center',
//           minWidth: 0 // ← required for flex+ellipsis
//         }}
//         onClick={() => list.length && sel.id && setShow(true)}
//       >
//         <span
//           style={{
//             display: 'inline-block',
//             maxWidth: '100%',
//             whiteSpace: 'nowrap',
//             overflow: 'hidden',
//             textOverflow: 'ellipsis',
//             textAlign: 'center'
//           }}
//         >
//           { sel.id ? up(sel.name) : <Spinner animation="border" size="sm" /> }
//         </span>
//       </Button>

//       <Modal show={show} onHide={() => setShow(false)} fullscreen="sm-down" centered>
//         <Modal.Header closeButton>
//           <Modal.Title>Select Campus / Building</Modal.Title>
//         </Modal.Header>

//         <Modal.Body style={{ paddingTop: 0 }}>
//           <div className="px-3 py-2">
//             <FormControl
//               ref={useRef(null)}
//               placeholder="Search"
//               value={search}
//               onChange={e => setSearch(e.target.value)}
//             />
//           </div>

//           {/* “All Buildings” for admins, “All My Campuses (N)” for non-admins */}
//           <Form.Check
//             type="checkbox" id="chk_all"
//             className="px-3 py-2 fw-bold"
//             label={sentinelLabel}
//             checked={checked.length === list.length}
//             onChange={toggleAll}
//           />

//           {loading && (
//             <div className="d-flex justify-content-center py-3">
//               <Spinner animation="border" size="sm"/>
//             </div>
//           )}
//           {error && !loading && <div className="text-danger px-3 py-2">{error}</div>}

//           {!loading && !error && (
//             filtered.length
//               ? filtered.map(r => (
//                   <div key={r.iBuildingID}
//                        className="d-flex align-items-center border-bottom w-100"
//                        style={{ fontSize: 14, lineHeight: 1.3, padding: '8px 12px' }}>
//                     <Form.Check
//                       type="checkbox"
//                       id={`chk_${r.iBuildingID}`}
//                       className="me-2"
//                       checked={checked.includes(r.iBuildingID)}
//                       onChange={() => toggleCheck(r.iBuildingID)}
//                     />
//                     <span role="button"
//                           className="flex-grow-1 text-truncate"
//                           onClick={() => toggleCheck(r.iBuildingID)}>
//                       {r.BuildingName}
//                     </span>
//                   </div>
//                 ))
//               : <div className="px-3 py-2">No matching records</div>
//           )}
//         </Modal.Body>

//         <Modal.Footer className="justify-content-between">
//           <Button variant="secondary"
//                   onClick={() => { setChecked([]); setShow(false); }}>
//             Cancel
//           </Button>
//           <Button variant="primary"
//                   disabled={!checked.length}
//                   onClick={applyMulti}>
//             Apply&nbsp;{checked.length > 1 && `(${checked.length})`}
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   );
// }



// // src/components/DropdownSearchInfrastructureAlert.jsx
// import React, {
//   useState, useEffect, useContext, useRef,
// } from 'react';
// import {
//   Button, Modal, FormControl, Spinner,
// } from 'react-bootstrap';
// import axios from '../axiosAlertConfig';
// import { UserContext } from '../../userAlertContext';
// import Styles from './DropdownSearchInfrastructureAlert.module.css';

// /* ───────── helpers ───────── */
// const up = t => (t ?? '').toString().toUpperCase();

// /* ───────── component ───────── */
// export default function DropdownSearchInfrastructureAlert({
//   databaseContent = [],         // full list of campuses
//   selectedID      = '',         // controlled campus ID from parent
//   onSelect,                     // receives ({ id,name }) whenever changed
// }) {
//   const {
//     user,               // for initial fetch
//     iAccountID,
//     setIBuildingID,
//     setBuildingName,
//   } = useContext(UserContext);

//   const [show,   setShow]    = useState(false);
//   const [search, setSearch]  = useState('');
//   const [loading,setLoading]= useState(false);
//   const [error,  setError]   = useState(null);
//   const [list,   setList]    = useState(databaseContent);

//   // local “selected” state
//   const [sel, setSel] = useState({ id: '', name: 'Campus' });

//   // 1️⃣ On mount: fetch current user setting and seed sel
//   useEffect(() => {
//     if (!user?.iAccountID || !user?.userID) return;
//     let cancelled = false;
//     (async () => {
//       try {
//         const { data } = await axios.get(
//           '/UsersManagement/primaryCampusByUser',
//           { params: { iAccountID: user.iAccountID, iUserID: user.userID } }
//         );
//         if (cancelled) return;
//         // pick first of either new JSON or legacy fields
//         const id   = Array.isArray(data.campusIDs)   && data.campusIDs[0]   ? data.campusIDs[0]   : data.PrimaryCampusID;
//         const name = Array.isArray(data.campusNames) && data.campusNames[0] ? data.campusNames[0] : data.PrimaryCampusName;
//         const finalName = name || 'Campus';
//         setSel({ id, name: finalName });
//         setIBuildingID(id);
//         setBuildingName(finalName);
//         onSelect?.({ id, name: finalName });
//       } catch (err) {
//         console.error('[Dropdown] load primaryCampusByUser', err);
//       }
//     })();
//     return () => { cancelled = true; };
//   }, [ user?.iAccountID, user?.userID ]);

//   // 2️⃣ If parent ever changes selectedID prop, update local sel.name
//   useEffect(() => {
//     if (!selectedID || selectedID === sel.id) return;
//     const match = databaseContent.find(b => b.iBuildingID === selectedID);
//     const name  = match ? match.BuildingName : 'Campus';
//     setSel({ id: selectedID, name });
//   }, [ selectedID, databaseContent ]);

//   // 3️⃣ Keep our list up-to-date
//   useEffect(() => {
//     setList(databaseContent);
//   }, [ databaseContent ]);

//   // focus search when opened
//   const searchRef = useRef();
//   useEffect(() => {
//     if (show) setTimeout(() => searchRef.current?.focus(), 100);
//   }, [ show ]);

//   // when user picks something from the modal
//   const applySel = (id, name) => {
//     setSel({ id, name });
//     setIBuildingID(id);
//     setBuildingName(name);
//     onSelect?.({ id, name });
//   };

//   // user clicked one of the building buttons
//   const choose = async id => {
//     setLoading(true);
//     setError(null);
//     try {
//       if (id === '-99') {
//         applySel('-99', 'Master');
//       } else {
//         const { data } = await axios.get(
//           '/buildinginfosBuildingID',
//           { params:{ iAccountID, iBuildingID: id } }
//         );
//         if (data?.length) {
//           applySel(id, data[0].BuildingName);
//         }
//       }
//       setShow(false);
//     } catch (e) {
//       console.error('[Dropdown] choose error', e);
//       setError('Network error');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const filtered = list.filter(b =>
//     b.BuildingName.toLowerCase().includes(search.toLowerCase())
//   );

//   return (
//     <>
//       {/* toggle button with truncation */}
//       <Button
//         variant="outline-primary"
//         className={Styles.toggleBtn}
//         title={sel.name}
//         style={{
//           boxSizing: 'border-box',
//           width: '100%',
//           padding: '4px',
//           fontSize: 16,
//           textAlign: 'left',
//           minWidth: 0,
//           whiteSpace: 'nowrap',
//           overflow: 'hidden',
//           textOverflow: 'ellipsis',
//           display: 'block',
//         }}
//         onClick={() => setShow(true)}
//       >
//         {up(sel.name)}
//       </Button>

//       {/* modal */}
//       <Modal
//         show={show}
//         onHide={() => setShow(false)}
//         fullscreen="sm-down"
//         centered
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>Select Campus / Building</Modal.Title>
//         </Modal.Header>
//         <Modal.Body style={{ paddingTop: 0 }}>
//           <div className="px-3 py-2">
//             <FormControl
//               ref={searchRef}
//               placeholder="Search"
//               value={search}
//               onChange={e => setSearch(e.target.value)}
//             />
//           </div>

//           {loading && (
//             <div className="d-flex justify-content-center py-3">
//               <Spinner animation="border" size="sm" />
//             </div>
//           )}
//           {error && !loading && (
//             <div className="text-danger px-3 py-2">{error}</div>
//           )}
//           {!loading && !error && (
//             filtered.length
//               ? filtered.map(b => (
//                   <Button
//                     key={b.iBuildingID}
//                     variant="light"
//                     className="border-bottom w-100 text-start"
//                     style={{
//                       fontSize: 14,
//                       lineHeight: 1.3,
//                       padding: '8px 12px',
//                     }}
//                     onClick={() => choose(b.iBuildingID)}
//                   >
//                     {b.BuildingName}
//                   </Button>
//                 ))
//               : <div className="px-3 py-2">No matching records</div>
//           )}
//         </Modal.Body>
//       </Modal>
//     </>
//   );
// }

 
// /* ────────────────────────────────────────────────────────────────
//  *  DropdownSearchInfrastructureAlert.jsx
//  *  • Non-admins see ONLY their primary campus(es) (Users.campusIDs)
//  *  • Admins see all buildings
//  *  • “All Buildings” becomes “All My Campuses (N)” for non-admins
//  *  • Keeps existing fast seed + slow fallback + context writes
//  * ──────────────────────────────────────────────────────────────── */
// import React, { useState, useEffect, useContext, useRef } from 'react';
// import { Button, Modal, FormControl, Spinner, Form } from 'react-bootstrap';
// import axios             from '../axiosAlertConfig';
// import { UserContext }   from '../../userAlertContext';
// import Styles            from './DropdownSearchInfrastructureAlert.module.css';

// /* helpers */
// const up = s => (s ?? '').toString().toUpperCase();
// const idsFromSel = (v, list) =>
//   !v               ? []
//   : v === '-99'    ? list.map(r => r.iBuildingID)            // “All (my) campuses” sentinel
//                    : v.split(',').filter(Boolean);

// export default function DropdownSearchInfrastructureAlert({
//   databaseContent = [],             // rows from BuildingInfos
//   selectedID      = '',             // controlled value from parent
//   onSelect,                         // ({id,name}) => void
// }) {
//   const {
//     user,                /* FULL user object */
//     setUser,             /* <── we overwrite campusIDs/names here on fallback */
//     setIBuildingID,
//     setBuildingName,
//   } = useContext(UserContext);

//   /* role gate */
//   const isAdmin = ['administrator','systemadministrator']
//     .includes((user?.userRole || '').toLowerCase());

//   /* ───── basic ui state ───── */
//   const [show,    setShow]    = useState(false);
//   const [search,  setSearch]  = useState('');
//   const [loading, setLoading] = useState(false);
//   const [error,   setError]   = useState(null);

//   const [sel,     setSel]     = useState({
//     id:   sessionStorage.getItem('defaultCampusID')   || '',
//     name: sessionStorage.getItem('defaultCampusName') || 'Campus'
//   });

//   const [checked, setChecked] = useState([]);

//   /* ────────────────────────────────────────────────────────────
//    *  1️⃣ FAST-seed from user.campusIDs[0] / campusNames[0]
//    * ──────────────────────────────────────────────────────────── */
//   useEffect(() => {
//     const id   = user?.campusIDs?.[0];
//     const name = user?.campusNames?.[0];
//     if (id && id !== sel.id) {
//       setSel({ id, name: name ?? 'Campus' });
//       setIBuildingID(id);
//       setBuildingName(name ?? 'Campus');
//       onSelect?.({ id, name: name ?? 'Campus' });
//     }
//   }, [user?.campusIDs, user?.campusNames]); // reruns only if arrays change

//   /* ────────────────────────────────────────────────────────────
//    *  2️⃣ SLOW fallback – fetch from DB **once** if still blank
//    *     and patch back into UserContext
//    * ──────────────────────────────────────────────────────────── */
//   useEffect(() => {
//     if (sel.id || !user?.iAccountID || !user?.userID) return; // already have data
//     let cancelled = false;
//     (async () => {
//       try {
//         setLoading(true);
//         const { data } = await axios.get('/UsersManagement/primaryCampusByUser', {
//           params: { iAccountID: user.iAccountID, iUserID: user.userID },
//         });
//         if (cancelled) return;

//         const campusIDs   = Array.isArray(data.campusIDs)   ? data.campusIDs   : [];
//         const campusNames = Array.isArray(data.campusNames) ? data.campusNames : [];

//         const id   = campusIDs[0]   ?? '';
//         const name = campusNames[0] ?? 'Campus';

//         /* ⬇️  PATCH BACK INTO USER CONTEXT  */
//         if (campusIDs.length) {
//           setUser(prev => {
//             const merged = { ...prev, campusIDs, campusNames };
//             sessionStorage.setItem('userInfo', JSON.stringify(merged)); // persistence
//             return merged;
//           });
//         }

//         /* populate local selection */
//         if (id) {
//           setSel({ id, name });
//           setIBuildingID(id);
//           setBuildingName(name);
//           onSelect?.({ id, name });
//           sessionStorage.setItem('defaultCampusID', id);
//           sessionStorage.setItem('defaultCampusName', name);
//         }
//       } catch (e) {
//         console.error('[Dropdown] primaryCampusByUser', e);
//         setError('Network error');
//       } finally {
//         setLoading(false);
//       }
//     })();
//     return () => { cancelled = true; };
//   }, [sel.id, user?.iAccountID, user?.userID, setUser, setIBuildingID, setBuildingName, onSelect]);

//   /* ────────────────────────────────────────────────────────────
//    *  Filter visible list based on role:
//    *    • Admins → all buildings.
//    *    • Non-admins → ONLY their campusIDs.
//    *  Also compute sentinel label (“All Buildings” vs “All My Campuses (N)”)
//    * ──────────────────────────────────────────────────────────── */
//   const allowedIDs = React.useMemo(() => {
//     const allIDs  = databaseContent.map(r => String(r.iBuildingID));
//     const userIDs = Array.isArray(user?.campusIDs) ? user.campusIDs.map(String) : [];
//     return isAdmin ? allIDs : userIDs;
//   }, [databaseContent, user?.campusIDs, isAdmin]);

//   const allowedRows = React.useMemo(
//     () => databaseContent.filter(r => allowedIDs.includes(String(r.iBuildingID))),
//     [databaseContent, allowedIDs]
//   );

//   const sentinelLabel = isAdmin
//     ? 'All Buildings'
//     : `All My Campuses (${allowedRows.length})`;

//   // List of selectable rows (no sentinel inside the checkbox list)
//   const list = React.useMemo(() => {
//     // only allowed rows for this user
//     return [...allowedRows];
//   }, [allowedRows]);

//   /* ────────────────────────────────────────────────────────────
//    *  3️⃣ Parent → child sync (use **filtered list** for names)
//    * ──────────────────────────────────────────────────────────── */
//   useEffect(() => {
//     if (!selectedID) return;

//     const ids =
//       selectedID === '-99'
//         ? list.map(r => r.iBuildingID) // "All My Campuses" after filtering
//         : selectedID.split(',').filter(Boolean);

//     const names = list
//       .filter(r => ids.includes(r.iBuildingID))
//       .map(r => r.BuildingName);

//     const nm = selectedID === '-99' ? sentinelLabel : names.join(', ');

//     if (selectedID !== sel.id || nm !== sel.name) {
//       setSel({ id: selectedID, name: nm });
//     }
//   }, [selectedID, list, sentinelLabel]); // avoid sel.* to prevent loops

//   /* prepare check-boxes whenever modal opens */
//   useEffect(() => {
//     if (show) setChecked(idsFromSel(sel.id, list));
//   }, [show, sel.id, list]);

//   /* helpers */
//   const toggleCheck = id =>
//     setChecked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

//   const toggleAll = () => {
//     setChecked(p => (p.length === list.length ? [] : list.map(r => r.iBuildingID)));
//   };

//   const applySel = (id, name, idsArray = null, listRef = []) => {
//     setSel({ id, name });

//     // For global context, keep a single id (first real building)
//     const firstRealIDs = Array.isArray(idsArray) ? idsArray : [];
//     const firstID =
//       firstRealIDs.find(x => x && x !== '-99') ||
//       ((id && id !== '-99' && !id.includes(',')) ? id : (user?.campusIDs?.[0] ?? ''));

//     if (firstID) {
//       const firstName =
//         listRef.find(r => r.iBuildingID === firstID)?.BuildingName || name || 'Campus';
//       setIBuildingID(firstID);
//       setBuildingName(firstName);
//     }

//     onSelect?.({ id, name });
//     sessionStorage.setItem('defaultCampusID', id);
//     sessionStorage.setItem('defaultCampusName', name);
//   };

//   const applyMulti = () => {
//     if (!checked.length) return;

//     if (checked.length === list.length) {
//       // “All” = all **allowed** rows only (no sentinel id included)
//       applySel('-99', sentinelLabel, list.map(r => r.iBuildingID), list);
//     } else if (checked.length === 1) {
//       const id = checked[0];
//       const nm = list.find(r => r.iBuildingID === id)?.BuildingName || '';
//       applySel(id, nm, [id], list);
//     } else {
//       const ids = checked;
//       const names = list
//         .filter(r => ids.includes(r.iBuildingID))
//         .map(r => r.BuildingName);
//       applySel(ids.join(','), names.join(', '), ids, list);
//     }

//     setShow(false);
//   };

//   const filtered = list.filter(r =>
//     r.BuildingName.toLowerCase().includes(search.toLowerCase()),
//   );

//   const titleText = React.useMemo(() => {
//     if (!sel.id) return sel.name;
//     if (sel.id === '-99') return sentinelLabel;
//     const ids = idsFromSel(sel.id, list);
//     const names = list.filter(r => ids.includes(r.iBuildingID)).map(r => r.BuildingName);
//     return names.join(', ');
//   }, [sel.id, sel.name, list, sentinelLabel]);

//   /* ─── UI ─── */
//   return (
//     <>
//       <Button
//         variant="outline-primary"
//         className={Styles.toggleBtn}
//         title={titleText}
//         style={{
//           boxSizing: 'border-box',
//           width: '100%',
//           padding: 4,
//           fontSize: 16,
//           display: 'flex',
//           alignItems: 'center',
//           justifyContent: 'center',
//           minWidth: 0 // ← required for flex+ellipsis
//         }}
//         onClick={() => list.length && sel.id && setShow(true)}
//       >
//         <span
//           style={{
//             display: 'inline-block',
//             maxWidth: '100%',
//             whiteSpace: 'nowrap',
//             overflow: 'hidden',
//             textOverflow: 'ellipsis',
//             textAlign: 'center'
//           }}
//         >
//           { sel.id ? up(sel.name) : <Spinner animation="border" size="sm" /> }
//         </span>
//       </Button>

//       <Modal show={show} onHide={() => setShow(false)} fullscreen="sm-down" centered>
//         <Modal.Header closeButton>
//           <Modal.Title>Select Campus / Building</Modal.Title>
//         </Modal.Header>

//         <Modal.Body style={{ paddingTop: 0 }}>
//           <div className="px-3 py-2">
//             <FormControl
//               ref={useRef(null)}
//               placeholder="Search"
//               value={search}
//               onChange={e => setSearch(e.target.value)}
//             />
//           </div>

//           {/* “All Buildings” for admins, “All My Campuses (N)” for non-admins */}
//           <Form.Check
//             type="checkbox" id="chk_all"
//             className="px-3 py-2 fw-bold"
//             label={sentinelLabel}
//             checked={checked.length === list.length}
//             onChange={toggleAll}
//           />

//           {loading && (
//             <div className="d-flex justify-content-center py-3">
//               <Spinner animation="border" size="sm"/>
//             </div>
//           )}
//           {error && !loading && <div className="text-danger px-3 py-2">{error}</div>}

//           {!loading && !error && (
//             filtered.length
//               ? filtered.map(r => (
//                   <div key={r.iBuildingID}
//                        className="d-flex align-items-center border-bottom w-100"
//                        style={{ fontSize: 14, lineHeight: 1.3, padding: '8px 12px' }}>
//                     <Form.Check
//                       type="checkbox"
//                       id={`chk_${r.iBuildingID}`}
//                       className="me-2"
//                       checked={checked.includes(r.iBuildingID)}
//                       onChange={() => toggleCheck(r.iBuildingID)}
//                     />
//                     <span role="button"
//                           className="flex-grow-1 text-truncate"
//                           onClick={() => toggleCheck(r.iBuildingID)}>
//                       {r.BuildingName}
//                     </span>
//                   </div>
//                 ))
//               : <div className="px-3 py-2">No matching records</div>
//           )}
//         </Modal.Body>

//         <Modal.Footer className="justify-content-between">
//           <Button variant="secondary"
//                   onClick={() => { setChecked([]); setShow(false); }}>
//             Cancel
//           </Button>
//           <Button variant="primary"
//                   disabled={!checked.length}
//                   onClick={applyMulti}>
//             Apply&nbsp;{checked.length > 1 && `(${checked.length})`}
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   );
// }



// // src/components/DropdownSearchInfrastructureAlert.jsx
// import React, {
//   useState, useEffect, useContext, useRef,
// } from 'react';
// import {
//   Button, Modal, FormControl, Spinner,
// } from 'react-bootstrap';
// import axios from '../axiosAlertConfig';
// import { UserContext } from '../../userAlertContext';
// import Styles from './DropdownSearchInfrastructureAlert.module.css';

// /* ───────── helpers ───────── */
// const up = t => (t ?? '').toString().toUpperCase();

// /* ───────── component ───────── */
// export default function DropdownSearchInfrastructureAlert({
//   databaseContent = [],         // full list of campuses
//   selectedID      = '',         // controlled campus ID from parent
//   onSelect,                     // receives ({ id,name }) whenever changed
// }) {
//   const {
//     user,               // for initial fetch
//     iAccountID,
//     setIBuildingID,
//     setBuildingName,
//   } = useContext(UserContext);

//   const [show,   setShow]    = useState(false);
//   const [search, setSearch]  = useState('');
//   const [loading,setLoading]= useState(false);
//   const [error,  setError]   = useState(null);
//   const [list,   setList]    = useState(databaseContent);

//   // local “selected” state
//   const [sel, setSel] = useState({ id: '', name: 'Campus' });

//   // 1️⃣ On mount: fetch current user setting and seed sel
//   useEffect(() => {
//     if (!user?.iAccountID || !user?.userID) return;
//     let cancelled = false;
//     (async () => {
//       try {
//         const { data } = await axios.get(
//           '/UsersManagement/primaryCampusByUser',
//           { params: { iAccountID: user.iAccountID, iUserID: user.userID } }
//         );
//         if (cancelled) return;
//         // pick first of either new JSON or legacy fields
//         const id   = Array.isArray(data.campusIDs)   && data.campusIDs[0]   ? data.campusIDs[0]   : data.PrimaryCampusID;
//         const name = Array.isArray(data.campusNames) && data.campusNames[0] ? data.campusNames[0] : data.PrimaryCampusName;
//         const finalName = name || 'Campus';
//         setSel({ id, name: finalName });
//         setIBuildingID(id);
//         setBuildingName(finalName);
//         onSelect?.({ id, name: finalName });
//       } catch (err) {
//         console.error('[Dropdown] load primaryCampusByUser', err);
//       }
//     })();
//     return () => { cancelled = true; };
//   }, [ user?.iAccountID, user?.userID ]);

//   // 2️⃣ If parent ever changes selectedID prop, update local sel.name
//   useEffect(() => {
//     if (!selectedID || selectedID === sel.id) return;
//     const match = databaseContent.find(b => b.iBuildingID === selectedID);
//     const name  = match ? match.BuildingName : 'Campus';
//     setSel({ id: selectedID, name });
//   }, [ selectedID, databaseContent ]);

//   // 3️⃣ Keep our list up-to-date
//   useEffect(() => {
//     setList(databaseContent);
//   }, [ databaseContent ]);

//   // focus search when opened
//   const searchRef = useRef();
//   useEffect(() => {
//     if (show) setTimeout(() => searchRef.current?.focus(), 100);
//   }, [ show ]);

//   // when user picks something from the modal
//   const applySel = (id, name) => {
//     setSel({ id, name });
//     setIBuildingID(id);
//     setBuildingName(name);
//     onSelect?.({ id, name });
//   };

//   // user clicked one of the building buttons
//   const choose = async id => {
//     setLoading(true);
//     setError(null);
//     try {
//       if (id === '-99') {
//         applySel('-99', 'Master');
//       } else {
//         const { data } = await axios.get(
//           '/buildinginfosBuildingID',
//           { params:{ iAccountID, iBuildingID: id } }
//         );
//         if (data?.length) {
//           applySel(id, data[0].BuildingName);
//         }
//       }
//       setShow(false);
//     } catch (e) {
//       console.error('[Dropdown] choose error', e);
//       setError('Network error');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const filtered = list.filter(b =>
//     b.BuildingName.toLowerCase().includes(search.toLowerCase())
//   );

//   return (
//     <>
//       {/* toggle button with truncation */}
//       <Button
//         variant="outline-primary"
//         className={Styles.toggleBtn}
//         title={sel.name}
//         style={{
//           boxSizing: 'border-box',
//           width: '100%',
//           padding: '4px',
//           fontSize: 16,
//           textAlign: 'left',
//           minWidth: 0,
//           whiteSpace: 'nowrap',
//           overflow: 'hidden',
//           textOverflow: 'ellipsis',
//           display: 'block',
//         }}
//         onClick={() => setShow(true)}
//       >
//         {up(sel.name)}
//       </Button>

//       {/* modal */}
//       <Modal
//         show={show}
//         onHide={() => setShow(false)}
//         fullscreen="sm-down"
//         centered
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>Select Campus / Building</Modal.Title>
//         </Modal.Header>
//         <Modal.Body style={{ paddingTop: 0 }}>
//           <div className="px-3 py-2">
//             <FormControl
//               ref={searchRef}
//               placeholder="Search"
//               value={search}
//               onChange={e => setSearch(e.target.value)}
//             />
//           </div>

//           {loading && (
//             <div className="d-flex justify-content-center py-3">
//               <Spinner animation="border" size="sm" />
//             </div>
//           )}
//           {error && !loading && (
//             <div className="text-danger px-3 py-2">{error}</div>
//           )}
//           {!loading && !error && (
//             filtered.length
//               ? filtered.map(b => (
//                   <Button
//                     key={b.iBuildingID}
//                     variant="light"
//                     className="border-bottom w-100 text-start"
//                     style={{
//                       fontSize: 14,
//                       lineHeight: 1.3,
//                       padding: '8px 12px',
//                     }}
//                     onClick={() => choose(b.iBuildingID)}
//                   >
//                     {b.BuildingName}
//                   </Button>
//                 ))
//               : <div className="px-3 py-2">No matching records</div>
//           )}
//         </Modal.Body>
//       </Modal>
//     </>
//   );
// }

 
